/**
 * Skeleton — el esqueleto que ocupa el lugar de lo que todavía no llegó.
 *
 * ⚠️ Convive con `components/Skeleton.tsx`, que usan 26 archivos. Lo que aporta
 * este es la variante **`table`**, que dibuja filas con cuatro columnas de
 * anchos distintos en vez de barras iguales: el objetivo de un esqueleto es que
 * el contenido no salte al llegar, y para eso tiene que parecerse a lo que
 * viene.
 *
 * ⚠️ Lleva `role="status"` y un texto en `.cl-sr-only`. Sin eso, para un lector
 * de pantalla la página está simplemente **vacía** mientras carga: las barras
 * grises son `<div>` sin contenido y no anuncian nada.
 *
 * 🔴 No parpadea. Un esqueleto animado sobre las cuatro superficies de vidrio
 * —que ya tienen su propio movimiento de fondo con los orbes— compite con los
 * datos que están llegando.
 */
import type { CSSProperties, HTMLAttributes } from 'react'

export type SkeletonVariant = 'text' | 'table' | 'block'

export interface SkeletonProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'className' | 'style'> {
  variant?: SkeletonVariant
  /** Cuántas líneas o filas. */
  lines?: number
  height?: number | string
  width?: number | string
  className?: string
  style?: CSSProperties
}

const BARRA: CSSProperties = {
  background: 'rgb(var(--rgb-glass) / var(--alpha-glass-3))',
  borderRadius: 8,
}

export function Skeleton({
  variant = 'text',
  lines = 3,
  height,
  width,
  className = '',
  style,
  ...rest
}: SkeletonProps) {
  if (variant === 'table') {
    return (
      <div className={className} role="status" aria-live="polite" style={style} {...rest}>
        <span className="cl-sr-only">Cargando datos…</span>
        {Array.from({ length: lines }).map((_, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              gap: 12,
              padding: '9px 14px',
              borderBottom: 'var(--border-hairline)',
            }}
          >
            {/* Anchos desparejos a propósito: una tabla real no tiene cuatro
                columnas iguales, y si el esqueleto las tiene el contenido salta
                al llegar. */}
            {[2, 3, 1, 1].map((flex, j) => (
              <div key={j} style={{ ...BARRA, height: 12, flex }} />
            ))}
          </div>
        ))}
      </div>
    )
  }

  if (variant === 'block') {
    return (
      <div
        className={className}
        role="status"
        aria-live="polite"
        style={{ ...BARRA, height: height || 120, width: width || '100%', ...style }}
        {...rest}
      >
        <span className="cl-sr-only">Cargando…</span>
      </div>
    )
  }

  return (
    <div className={className} role="status" aria-live="polite" style={style} {...rest}>
      <span className="cl-sr-only">Cargando…</span>
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          style={{
            ...BARRA,
            height: 12,
            marginBottom: 8,
            // La última corta: es lo que hace que se lea como un párrafo y no
            // como una tabla.
            width: i === lines - 1 ? '62%' : '100%',
          }}
        />
      ))}
    </div>
  )
}
